import type { DragHandleProps } from "@repo/ui/components/kibo-ui/list";
import { SortableItem, SortableList, SortableProvider } from "@repo/ui/components/kibo-ui/list";
import { Badge } from "@repo/ui/components/ui/badge";
import { Button } from "@repo/ui/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@repo/ui/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@repo/ui/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@repo/ui/components/ui/sheet";
import { Status, StatusLabel } from "@repo/ui/components/ui/status";
import { cn } from "@repo/ui/lib/utils";
import { ChevronRight, ChevronsDownUp, ChevronsUpDown, Copy, GripVertical, Trash2 } from "lucide-react";
import { createContext, type ReactNode, use, useMemo, useState } from "react";
import { AddCatalogItemButton } from "@/shared/common/add-catalog-item-button";

export interface BuilderTreeAddOption {
  value: string;
  label: string;
}

export interface BuilderTreeNode {
  id: string;
  title: string;
  kindLabel?: string;
  managed?: boolean;
  summary?: ReactNode;
  incomplete?: boolean;
  badges?: string[];
  isContainer?: boolean;
  children?: BuilderTreeNode[];
  canRemove?: boolean;
  canDuplicate?: boolean;
}

interface BuilderTreeActions {
  select?: (id: string) => void;
  add?: (parentId: string | null, kind?: string) => void;
  move?: (parentId: string | null, fromIndex: number, toIndex: number) => void;
  remove?: (id: string) => void;
  duplicate?: (id: string) => void;
}

interface BuilderTreeContextValue {
  state: { nodes: BuilderTreeNode[]; selectedId: string | null; allExpanded: boolean | undefined };
  actions: BuilderTreeActions & { toggleAll: () => void };
  meta: {
    collectionLabel: string;
    addLabel: (parent: BuilderTreeNode | null) => string;
    addOptions?: (parent: BuilderTreeNode | null) => BuilderTreeAddOption[];
    disabled: boolean;
  };
}

const BuilderTreeContext = createContext<BuilderTreeContextValue | null>(null);

function useBuilderTree(component: string): BuilderTreeContextValue {
  const value = use(BuilderTreeContext);
  if (!value) throw new Error(`BuilderTree.${component} must be rendered inside BuilderTree.Provider`);
  return value;
}

function Provider({
  nodes,
  selectedId,
  actions,
  collectionLabel,
  addLabel,
  addOptions,
  disabled = false,
  children,
}: {
  nodes: BuilderTreeNode[];
  selectedId: string | null;
  actions: BuilderTreeActions;
  collectionLabel: string;
  addLabel: (parent: BuilderTreeNode | null) => string;
  /** More than one option turns the add button into a menu; the picked value reaches `actions.add`. */
  addOptions?: (parent: BuilderTreeNode | null) => BuilderTreeAddOption[];
  disabled?: boolean;
  children: ReactNode;
}) {
  const [allExpanded, setAllExpanded] = useState<boolean | undefined>(undefined);
  const value = useMemo<BuilderTreeContextValue>(
    () => ({
      state: { nodes, selectedId, allExpanded },
      actions: { ...actions, toggleAll: () => setAllExpanded((current) => !current) },
      meta: { collectionLabel, addLabel, addOptions, disabled },
    }),
    [nodes, selectedId, allExpanded, actions, collectionLabel, addLabel, addOptions, disabled],
  );
  return <BuilderTreeContext value={value}>{children}</BuilderTreeContext>;
}

function Header() {
  const { state, actions, meta } = useBuilderTree("Header");
  const hasContainers = state.nodes.some((node) => node.isContainer);
  return (
    <div className="mb-2 flex items-center justify-between">
      <h2 className="text-sm font-medium text-muted-foreground">
        {meta.collectionLabel}
        {state.nodes.length > 0 && ` (${state.nodes.length})`}
      </h2>
      {hasContainers && (
        <Button type="button" variant="ghost" size="sm" onClick={actions.toggleAll}>
          {state.allExpanded ? (
            <>
              <ChevronsDownUp /> Collapse all
            </>
          ) : (
            <>
              <ChevronsUpDown /> Expand all
            </>
          )}
        </Button>
      )}
    </div>
  );
}

function NodeCard({
  node,
  dragHandleProps,
  children,
}: {
  node: BuilderTreeNode;
  dragHandleProps?: DragHandleProps;
  children?: ReactNode;
}) {
  const { state, actions, meta } = useBuilderTree("NodeCard");
  const [localOpen, setLocalOpen] = useState(false);
  const isOpen = state.allExpanded ?? localOpen;
  const selected = state.selectedId === node.id;
  const content = (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
      <div className="flex min-w-0 items-center gap-2">
        <span className="min-w-0 flex-1 truncate font-medium">{node.title || "Untitled"}</span>
        {node.kindLabel && (
          <Badge variant="secondary" className="px-1.5 py-0 text-xs text-muted-foreground">
            {node.kindLabel}
          </Badge>
        )}
        {node.badges?.map((badge) => (
          <Badge key={badge} variant="secondary" className="px-1.5 py-0 text-xs text-muted-foreground">
            {badge}
          </Badge>
        ))}
        {node.incomplete && (
          <span className="shrink-0 text-xs font-normal text-amber-700 dark:text-amber-400">Incomplete</span>
        )}
      </div>
      {node.summary && (
        <div className="whitespace-normal break-words text-xs font-normal text-muted-foreground">{node.summary}</div>
      )}
    </div>
  );
  const row = (
    <div
      className={cn(
        "relative flex min-h-12 min-w-0 items-center gap-1 rounded-md p-3",
        actions.select && !node.managed && "hover:bg-muted/50",
        node.managed && "pr-20",
      )}
    >
      {node.managed && (
        <Status variant="warning" className="absolute top-2 right-2">
          <StatusLabel>Managed</StatusLabel>
        </Status>
      )}
      {dragHandleProps && (
        <div
          ref={dragHandleProps.ref}
          className={cn(
            "relative z-10 text-muted-foreground",
            meta.disabled ? "cursor-default" : "cursor-grab hover:text-foreground",
          )}
        >
          <GripVertical className="size-4" />
        </div>
      )}
      {node.isContainer && (
        <CollapsibleTrigger className="relative z-10 p-0.5">
          <ChevronRight className={cn("size-3.5 transition-transform", isOpen && "rotate-90")} />
        </CollapsibleTrigger>
      )}
      {actions.select && !node.managed ? (
        <Button
          type="button"
          variant="ghost"
          className="flex h-auto min-w-0 flex-1 items-center justify-start gap-2 p-0 hover:bg-transparent after:absolute after:inset-0 after:rounded-md focus-visible:ring-0 focus-visible:after:ring-2 focus-visible:after:ring-ring"
          onClick={() => actions.select?.(node.id)}
        >
          {content}
          {!node.isContainer && <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />}
        </Button>
      ) : (
        content
      )}

      <div className="relative z-10 flex shrink-0 items-center gap-0.5">
        {!node.managed && actions.duplicate && node.canDuplicate !== false && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-6"
            aria-label={`Duplicate ${node.title || node.kindLabel || "item"}`}
            disabled={meta.disabled}
            onClick={() => actions.duplicate?.(node.id)}
          >
            <Copy className="size-3" />
          </Button>
        )}
        {!node.managed && actions.remove && node.canRemove !== false && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-6 text-destructive hover:text-destructive"
            aria-label={`Remove ${node.title || node.kindLabel || "item"}`}
            disabled={meta.disabled}
            onClick={() => actions.remove?.(node.id)}
          >
            <Trash2 className="size-3" />
          </Button>
        )}
      </div>
    </div>
  );
  const className = cn(
    "min-w-0 rounded-md border bg-background text-sm transition-colors",
    selected && !node.managed && "ring-2 ring-primary",
  );
  if (!node.isContainer) return <div className={className}>{row}</div>;
  return (
    <Collapsible open={isOpen} onOpenChange={setLocalOpen} className={className}>
      {row}
      <CollapsibleContent className="space-y-2 border-t px-3 pt-2 pb-3">{children}</CollapsibleContent>
    </Collapsible>
  );
}

function ManagedCard({ title, summary, badge = "Managed" }: { title: string; summary?: ReactNode; badge?: string }) {
  return (
    <div className="relative min-w-0 rounded-md border bg-background text-sm">
      <Status variant="warning" className="absolute top-2 right-2">
        <StatusLabel>{badge}</StatusLabel>
      </Status>
      <div className="flex min-h-12 min-w-0 flex-col gap-0.5 p-3 pr-20 text-left">
        <span className="min-w-0 truncate font-medium">{title}</span>
        {summary && (
          <div className="whitespace-normal break-words text-xs font-normal text-muted-foreground">{summary}</div>
        )}
      </div>
    </div>
  );
}

function NodeList({ nodes, parent }: { nodes: BuilderTreeNode[]; parent: BuilderTreeNode | null }) {
  const { actions, meta } = useBuilderTree("Items");
  const parentId = parent?.id ?? null;
  const handleDragEnd = (event: {
    canceled?: boolean;
    operation: { source?: { sortable?: { initialIndex: number; index: number } } };
  }) => {
    if (event.canceled) return;
    const sortable = event.operation.source?.sortable;
    if (!sortable || sortable.initialIndex === sortable.index) return;
    actions.move?.(parentId, sortable.initialIndex, sortable.index);
  };
  return (
    <div className="space-y-2">
      {actions.move ? (
        <SortableProvider onDragEnd={handleDragEnd}>
          <SortableList className="gap-2">
            {nodes.map((node, index) => (
              <SortableItem
                key={node.id}
                id={node.id}
                index={index}
                group={parentId ?? "root"}
                disabled={meta.disabled}
              >
                {({ dragHandleProps }) => (
                  <NodeCard node={node} dragHandleProps={dragHandleProps}>
                    {node.isContainer && <NodeList nodes={node.children ?? []} parent={node} />}
                  </NodeCard>
                )}
              </SortableItem>
            ))}
          </SortableList>
        </SortableProvider>
      ) : (
        <div className="space-y-2">
          {nodes.map((node) => (
            <NodeCard key={node.id} node={node}>
              {node.isContainer && <NodeList nodes={node.children ?? []} parent={node} />}
            </NodeCard>
          ))}
        </div>
      )}
      {actions.add && <AddButton parent={parent} />}
    </div>
  );
}

function AddButton({ parent }: { parent: BuilderTreeNode | null }) {
  const { actions, meta } = useBuilderTree("AddButton");
  const parentId = parent?.id ?? null;
  const options = meta.addOptions?.(parent) ?? [];
  const label = meta.addLabel(parent);
  if (options.length <= 1) {
    return (
      <AddCatalogItemButton
        label={label}
        onClick={() => actions.add?.(parentId, options[0]?.value)}
        disabled={meta.disabled}
      />
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={<AddCatalogItemButton label={label} disabled={meta.disabled} />} />
      <DropdownMenuContent align="center" className="w-48">
        {options.map((option) => (
          <DropdownMenuItem key={option.value} onClick={() => actions.add?.(parentId, option.value)}>
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Items() {
  const { state } = useBuilderTree("Items");
  return <NodeList nodes={state.nodes} parent={null} />;
}

function EditorSheet({
  open,
  onOpenChange,
  title,
  className,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className={cn("sm:!max-w-md", className)}>
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        {children}
      </SheetContent>
    </Sheet>
  );
}

export const BuilderTree = { Provider, Header, Items, ManagedCard, EditorSheet };
